import {OverlayState} from "@/hooks/common/useOverlay";
import {useTaggingClick} from "@/hooks/common/useTagging";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import * as style from "@COMPONENTS/style/detailpackage/couponselectpopup";
import PopupConfirm, {PopupButton, PopupButtonWrap} from "@FRAMEWORK/ui/desktop/popup/PopupConfirm";
import RadioN from "@FRAMEWORK/ui/desktop/input/RadioN";
import useCustomerMe from "@QUERIES/account/useCustomerMe";
import useCouponByUser, {CouponByUser} from "@QUERIES/prdt/coupon/useCouponByUser";
import couponDownAsync, {RequestBody} from "@QUERIES/prdt/coupon/useCouponDown";
import {useQueryClient} from "@tanstack/react-query";
import {format} from "@yb/utils";
import dayjs from "dayjs";
import React, {memo, useCallback, useEffect, useId, useMemo, useRef, useState} from "react";

/** 선착순 쿠폰 마감임박 잔여 수량 기준 */
const FCFS_IMMINENT_REMAIN_CNT = 20;

export type CouponPopType = CouponByUser & {calcObj?: {totalPrice: number; label: string}; tourListNo?: number};
interface Props extends OverlayState {
	evCd: string;
	count: {A: number; C: number; I: number};
	callback: (coupon: Array<CouponPopType>) => void;
}

interface CouponProps extends CouponPopType {
	index: number;
	handleRadio: (e: React.ChangeEvent<HTMLInputElement>) => void;
	webCustNo: string;
	evCd: string;
}

const CouponItem = (props: CouponProps) => {
	const {
		index,
		calcObj,
		couponNo,
		couponNm,
		couponDivi,
		downYn,
		discDiviCd,
		discAmt,
		discRate,
		fcfsYn,
		remainCnt,
		handleRadio,
		webCustNo,
		evCd,
	} = props;
	const nameText = props.multiUseYn === "Y" ? "duplicateCoupon" : "availableCoupon";
	const id = useId();
	const radioId = `${nameText}${id}${index}`;
	const queryClient = useQueryClient();

	const isFcfs = fcfsYn === "Y";
	const remain = Number(remainCnt);
	const isSoldOut = isFcfs && remain <= 0;
	const isImpending = isFcfs && !isSoldOut && remain <= FCFS_IMMINENT_REMAIN_CNT;
	const needDownload = downYn === "Y" && couponDivi === "BA" && !isSoldOut;
	const isRadioDisabled = isSoldOut || (downYn === "Y" && couponDivi === "BA");

	const handleDown = async (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
		if (isSoldOut) return;
		try {
			const body: RequestBody = {
				compId: "1",
				webCustNo,
				couponNo,
			};
			const result = await couponDownAsync(body);
			const {data} = result;
			if (data.errorCode === "0000") {
				queryClient.refetchQueries({queryKey: ["useCouponByUser"], type: "active"});
			} else {
				alert(data.errorMsg);
			}
		} catch (error) {
			alert("오류가 발생하였습니다.");
		}
	};

	const {taggingClick} = useTaggingClick("goods_down_coupon", {evCd, selectDiv: "availableCoupon"});

	const discountBadge = discDiviCd === "M" ? `${format.koKRFormatNumber(discAmt)}원` : `${discRate}%`;

	let downloadButton = (
		<style.CouponDownloadBtn type="button" downloaded>
			<p>사용가능</p>
		</style.CouponDownloadBtn>
	);
	if (isSoldOut) {
		downloadButton = (
			<style.CouponDownloadBtn type="button" soldOut disabled>
				<p>마감</p>
			</style.CouponDownloadBtn>
		);
	} else if (needDownload) {
		downloadButton = (
			<style.CouponDownloadBtn
				type="button"
				onClick={e => {
					handleDown(e);
					taggingClick(props);
				}}
			>
				<style.CouponDownloadIcon />
				<p>쿠폰받기</p>
			</style.CouponDownloadBtn>
		);
	}

	return (
		<style.CouponListItem>
			<style.CouponItem htmlFor={radioId}>
				<style.CouponInfo>
					<RadioN
						id={radioId}
						name={nameText}
						value={calcObj.totalPrice}
						disabled={isRadioDisabled}
						onChange={handleRadio}
						data-props={JSON.stringify(props)}
					/>
					<div>
						<style.CouponText>
							<style.CouponBadge>
								{discountBadge}
								{isFcfs && (
									<style.CouponTagGroup>
										<style.CouponTag type="limited">선착순사용</style.CouponTag>
										{isImpending && <style.CouponTag type="impending">마감임박</style.CouponTag>}
									</style.CouponTagGroup>
								)}
							</style.CouponBadge>
							<style.CouponName>{couponNm}</style.CouponName>
						</style.CouponText>
						{isFcfs && (
							<style.CouponNotice>
								{isSoldOut ? "선착순사용 가능 수량이 모두 소진되었습니다." : "예약완료 전 수량이 마감 될 수 있습니다."}
							</style.CouponNotice>
						)}
					</div>
				</style.CouponInfo>
				{downloadButton}
			</style.CouponItem>
		</style.CouponListItem>
	);
};

const CouponPopupComponent = (props: Props) => {
	const {evCd, count, callback, show = true, close} = props;
	const {data: loginInfo} = useCustomerMe();
	const webCustNo = loginInfo.body?.webCustNo;

	const couponFetch = useCouponByUser(evCd, webCustNo);
	const coupon: Array<CouponPopType> = couponFetch.data?.body;

	const availableRef = useRef<HTMLUListElement>();
	const duplicateRef = useRef<HTMLUListElement>();
	const summaryRef = useRef<HTMLDivElement>(null);

	const [discount, setDiscount] = useState({availableCoupon: 0, duplicateCoupon: 0});
	const [tooltip, setTooltip] = useState(false);

	useEffect(() => {
		if (!tooltip) return;
		const handleClickOutside = (e: MouseEvent) => {
			if (summaryRef.current && e.target instanceof Node && !summaryRef.current.contains(e.target)) {
				setTooltip(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [tooltip]);

	const totalPrice = useCallback(
		(item: CouponPopType) => {
			const adtPrice = Number(item.evAdtPrice); // 성인 총상품가
			const chdPrice = Number(item.evChdPrice); // 소아 총상품가
			const infPrice = Number(item.evInfPrice); // 유아 총상품가

			const adtPriceToT = adtPrice * count.A; // 성인 합산가
			const chdPriceToT = chdPrice * count.C; // 소아 합산가
			const infPriceToT = infPrice * count.I; // 유아 합산가

			return adtPriceToT + chdPriceToT + infPriceToT; // 기본상품가 토탈 합산
		},
		[count],
	);

	const discountPrice = (_data: CouponPopType) => {
		let totalDiscountPrice: number; // 할인 금액
		let discountInfo: string; // 성인할인가
		const adtCnt = count.A;
		const chdCnt = count.C;
		const infCnt = count.I;
		if (_data.discDiviCd === "M") {
			// 정액
			totalDiscountPrice = _data.discAmt;
			discountInfo = "(정액할인)";
		} else {
			// 정율
			let adtPriceDiscount = Math.round((_data.evAdtPrice * _data.discRate) / 100);
			let chdPriceDiscount = Math.round((_data.evChdPrice * _data.discRate) / 100);
			let infPriceDiscount = Math.round((_data.evInfPrice * _data.discRate) / 100);
			discountInfo = `(${_data.discRate}%할인)`;
			totalDiscountPrice = adtPriceDiscount * adtCnt + chdPriceDiscount * chdCnt + infPriceDiscount * infCnt;
			if (_data.goodsPriceCd === "TOT" && _data.goodsPriceFr > totalPrice(_data)) {
				totalDiscountPrice = 0;
			} else if (_data.goodsPriceCd === "PER") {
				_data.goodsPriceFr > _data.evAdtPrice && (adtPriceDiscount = 0);
				_data.goodsPriceFr > _data.evChdPrice && (chdPriceDiscount = 0);
				_data.goodsPriceFr > _data.evInfPrice && (infPriceDiscount = 0);
				totalDiscountPrice = adtPriceDiscount * adtCnt + chdPriceDiscount * chdCnt + infPriceDiscount * infCnt;
			}
		}
		return {
			totalPrice: totalDiscountPrice,
			label: `${format.koKRFormatNumber(totalDiscountPrice)}원${discountInfo}`,
		};
	};

	const initCoupon = useMemo(() => {
		if (!coupon) return {availableCoupon: [], duplicateCoupon: []};
		const sysdate = dayjs().format("YYYYMMDD");
		const memberGrade = loginInfo.body?.custGradeCd;

		const result = coupon
			.filter((item: CouponPopType) => {
				const remainingCouponCnt = item.issueCnt - item.issueCntTot;
				const isFcfsSoldOut = item.fcfsYn === "Y" && Number(item.remainCnt) <= 0;
				item.tourListNo = null;
				if (item.validityFrDt && item.validityToDt && !(item.validityFrDt <= sysdate && item.validityToDt >= sysdate))
					return false;
				// 선착순 사용마감 쿠폰은 목록에 노출 (라디오 비활성)
				if (item.downYn === "Y" && remainingCouponCnt === 0 && item.couponDivi === "BA" && !isFcfsSoldOut) return false;
				if (item.couponDivi !== "BA" && item.downYn === "Y") return false;
				if (item.custGradeCd && memberGrade !== item.custGradeCd) return false;
				if (item.goodsPriceCd === "TOT" && item.goodsPriceFr >= totalPrice(item)) return false;
				if (item.goodsPriceCd === "TOT" && item.goodsPriceTo > 0 && item.goodsPriceTo <= totalPrice(item)) return false;
				if (item.goodsPriceCd === "PER" && item.goodsPriceFr >= item.evAdtPrice) return false;
				if (item.goodsPriceCd === "PER" && item.goodsPriceTo > 0 && item.goodsPriceTo <= item.evAdtPrice) return false;
				if (item.discDiviCd === "M" && !item.goodsPriceCd && item.discAmt > item.evAdtPrice) return false;
				item.calcObj = discountPrice(item);
				if (item.discDiviCd !== "M" && !item.goodsPriceCd && item.calcObj.totalPrice > item.evAdtPrice) return false;
				return true;
			})
			.sort((a, b) => b.calcObj.totalPrice - a.calcObj.totalPrice);
		const availableCoupon = result.filter(v => v.multiUseYn !== "Y"); // 일반
		const duplicateCoupon = result.filter(v => v.multiUseYn === "Y"); // 중복
		return {availableCoupon, duplicateCoupon};
	}, [coupon]);

	const available = initCoupon.availableCoupon;
	const duplicate = initCoupon.duplicateCoupon;

	useEffect(() => {
		// 로그인 체크
		if (!(loginInfo.body.nonUser === false)) close?.();
	}, [loginInfo]);

	const couponDataSet = () => {
		const couponData = [];
		const _available = availableRef.current?.querySelector("input:checked");
		const _duplicate = duplicateRef.current?.querySelector("input:checked");
		if (_available && _available.getAttribute("data-props") !== "false") {
			couponData.push(JSON.parse(_available.getAttribute("data-props")));
		}
		if (_duplicate && _duplicate.getAttribute("data-props") !== "false") {
			couponData.push(JSON.parse(_duplicate.getAttribute("data-props")));
		}
		return couponData;
	};

	const handleRadio = (e: React.ChangeEvent<HTMLInputElement>) => {
		const {name, value} = e.target;
		setDiscount({...discount, [name]: value});
	};

	const handleSubmit = () => {
		const couponData = couponDataSet();
		typeof callback === "function" && callback(couponData);
		close?.();
	};

	const handleClose = () => {
		close?.();
	};

	return (
		<PopupConfirm open={!!show} title="할인 쿠폰 선택" onClose={handleClose}>
			<style.CouponSelectContent>
				<style.CouponSection>
					<style.CouponSectionTitle>할인쿠폰</style.CouponSectionTitle>
					{available.length > 0 ? (
						<style.CouponList ref={availableRef}>
							{available.map((item, index) => (
								<CouponItem
									{...item}
									key={index}
									index={index}
									handleRadio={handleRadio}
									webCustNo={webCustNo}
									evCd={evCd}
								/>
							))}
							<style.CouponListItem isNone>
								<RadioN
									id="availableCouponNot"
									name="availableCoupon"
									value={0}
									onChange={handleRadio}
									defaultChecked
									data-props={"false"}
								>
									쿠폰 사용 안함
								</RadioN>
							</style.CouponListItem>
						</style.CouponList>
					) : (
						<style.EmptyCouponbox>사용가능한 쿠폰이 없습니다.</style.EmptyCouponbox>
					)}
				</style.CouponSection>

				<style.CouponSection>
					<style.CouponSectionTitle>중복 할인 쿠폰</style.CouponSectionTitle>
					{duplicate.length > 0 ? (
						<style.CouponList ref={duplicateRef}>
							{duplicate.map((item, index) => (
								<CouponItem
									{...item}
									key={index + available.length}
									index={index + available.length}
									handleRadio={handleRadio}
									webCustNo={webCustNo}
									evCd={evCd}
								/>
							))}
							<style.CouponListItem isNone>
								<RadioN
									id="duplicateCouponNot"
									name="duplicateCoupon"
									value={0}
									onChange={handleRadio}
									defaultChecked
									data-props={"false"}
								>
									쿠폰 사용 안함
								</RadioN>
							</style.CouponListItem>
						</style.CouponList>
					) : (
						<style.EmptyCouponbox>사용가능한 쿠폰이 없습니다.</style.EmptyCouponbox>
					)}
				</style.CouponSection>

				<style.DiscountSummary>
					{tooltip && (
						<style.Tooltip ref={summaryRef}>
							<ul>
								<li>쿠폰은 노랑풍선 회원만 사용 가능합니다.</li>
								<li>쿠폰에 따라 사용 조건이 상이할 수 있으므로 상세 조건을 확인해주세요.</li>
								<li>쿠폰 할인 금액은 최종 결제금액에 따라 변동 될 수 있습니다.</li>
								<li>
									예약 완료 후 적용된 쿠폰은 변경 불가하며, 쿠폰 변경을 원할 경우
									<br />
									예약 취소 후 다시 예약해주세요.
								</li>
								<li>예약 후 인원 취소 및 변경 진행 시, 적용된 쿠폰은 자동 취소 됩니다.</li>
								<li>예약 취소 시 사용된 쿠폰은 복구가 불가합니다.</li>
							</ul>
						</style.Tooltip>
					)}
					<style.DiscountSummaryLabel>
						할인 금액
						<style.TooltipButton type="button" onClick={() => setTooltip(!tooltip)} />
					</style.DiscountSummaryLabel>
					<style.DiscountSummaryAmount>
						<em>-{format.koKRFormatNumber(Number(discount.availableCoupon) + Number(discount.duplicateCoupon))}</em> 원
					</style.DiscountSummaryAmount>
				</style.DiscountSummary>
			</style.CouponSelectContent>
			<PopupButtonWrap>
				<PopupButton types="cancle" onClick={handleClose}>
					취소
				</PopupButton>
				<PopupButton types="submit" onClick={handleSubmit}>
					선택완료
				</PopupButton>
			</PopupButtonWrap>
		</PopupConfirm>
	);
};

const CouponPopup = (props: Props) => (
	<ErrorBoundary>
		<CouponPopupComponent {...props} />
	</ErrorBoundary>
);

export default memo(CouponPopup);
